import { auth } from '../config/firebase';
import { 
  RecaptchaVerifier, 
  signInWithPhoneNumber, 
  ConfirmationResult,
  PhoneAuthProvider,
  signInWithCredential,
  UserCredential,
  onAuthStateChanged,
  User
} from 'firebase/auth';

export interface PhoneAuthResult {
  success: boolean;
  confirmationResult?: ConfirmationResult;
  error?: string;
}

export interface PhoneVerificationResult {
  success: boolean;
  user?: User;
  error?: string;
}

class PhoneAuthService {
  private recaptchaVerifier: RecaptchaVerifier | null = null;
  private confirmationResult: ConfirmationResult | null = null;

  // Initialize reCAPTCHA
  initializeRecaptcha(containerId: string): void {
    try {
      if (this.recaptchaVerifier) {
        this.recaptchaVerifier.clear();
      }

      this.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
        size: 'invisible',
        callback: (response: any) => {
          console.log('reCAPTCHA solved:', response);
        },
        'expired-callback': () => {
          console.log('reCAPTCHA expired');
        }
      });
    } catch (error) {
      console.error('Error initializing reCAPTCHA:', error);
    }
  }

  // Send SMS verification code
  async sendVerificationCode(phoneNumber: string): Promise<PhoneAuthResult> {
    try {
      if (!this.recaptchaVerifier) {
        return {
          success: false,
          error: 'reCAPTCHA chưa được khởi tạo'
        };
      }

      // Format phone number (ensure it starts with country code)
      const formattedPhone = phoneNumber.startsWith('+') ? phoneNumber : `+84${phoneNumber.replace(/^0/, '')}`;
      
      const confirmationResult = await signInWithPhoneNumber(
        auth, 
        formattedPhone, 
        this.recaptchaVerifier
      );
      
      this.confirmationResult = confirmationResult;
      
      return {
        success: true,
        confirmationResult
      };
    } catch (error: any) {
      console.error('Error sending verification code:', error);
      return {
        success: false,
        error: this.getErrorMessage(error)
      };
    }
  }

  // Verify SMS code
  async verifyCode(code: string): Promise<PhoneVerificationResult> {
    try {
      if (!this.confirmationResult) {
        return {
          success: false,
          error: 'Không tìm thấy phiên xác thực. Vui lòng gửi lại mã.'
        };
      }

      const result = await this.confirmationResult.confirm(code);
      
      return {
        success: true,
        user: result.user
      };
    } catch (error: any) {
      console.error('Error verifying code:', error);
      return {
        success: false,
        error: this.getErrorMessage(error)
      };
    }
  }

  // Get current user
  getCurrentUser(): User | null {
    return auth.currentUser;
  }

  // Get ID token for current user
  async getIdToken(forceRefresh: boolean = false): Promise<string | null> {
    try {
      const user = auth.currentUser;
      if (!user) {
        return null;
      }
      
      const idToken = await user.getIdToken(forceRefresh);
      return idToken;
    } catch (error) {
      console.error('Error getting ID token:', error);
      return null;
    }
  }

  // Listen to auth state changes
  onAuthStateChanged(callback: (user: User | null) => void): () => void {
    return onAuthStateChanged(auth, callback);
  }

  // Sign out
  async signOut(): Promise<void> {
    try {
      await auth.signOut();
      this.confirmationResult = null;
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }

  // Clean up reCAPTCHA
  cleanup(): void {
    if (this.recaptchaVerifier) {
      this.recaptchaVerifier.clear();
      this.recaptchaVerifier = null;
    }
    this.confirmationResult = null;
  }

  // Helper method to get user-friendly error messages
  private getErrorMessage(error: any): string {
    switch (error.code) {
      case 'auth/invalid-phone-number':
        return 'Số điện thoại không hợp lệ';
      case 'auth/missing-phone-number':
        return 'Vui lòng nhập số điện thoại';
      case 'auth/quota-exceeded':
        return 'Đã vượt quá giới hạn gửi SMS. Vui lòng thử lại sau.';
      case 'auth/user-disabled':
        return 'Tài khoản đã bị vô hiệu hóa';
      case 'auth/operation-not-allowed':
        return 'Xác thực số điện thoại chưa được bật';
      case 'auth/invalid-verification-code':
        return 'Mã xác thực không đúng';
      case 'auth/invalid-verification-id':
        return 'Mã xác thực đã hết hạn';
      case 'auth/too-many-requests':
        return 'Quá nhiều yêu cầu. Vui lòng thử lại sau.';
      case 'auth/network-request-failed':
        return 'Lỗi kết nối mạng. Vui lòng kiểm tra kết nối internet.';
      default:
        return error.message || 'Đã xảy ra lỗi không xác định';
    }
  }
}

export const phoneAuthService = new PhoneAuthService();
